import React, { useState } from "react";
import { Link } from "gatsby";
import { useToast } from "@/hooks/use-toast";
import { fireLead } from "@/lib/tracking";

interface EmailJSFormProps {
    className?: string;
    onSubmitStart?: () => void;
    onSubmitComplete?: () => void;
    defaultService?: string;
}

const EmailJSForm: React.FC<EmailJSFormProps> = ({
    className,
    onSubmitStart,
    onSubmitComplete,
    defaultService,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const { toast } = useToast();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const templateParams = {
            from_name: `${formData.get('firstName')} ${formData.get('lastName')}`,
            from_email: formData.get('email'),
            phone: formData.get('phone'),
            service: formData.get('service'),
            message: formData.get('message'),
            to_name: 'Y3 Smiles Dental',
        };

        onSubmitStart?.();
        setSubmitting(true);

        try {
            const response = await fetch('/api/send-enquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(templateParams)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `Failed with status ${response.status}`);
            }

            // ── Conversion tracking ─────────────────────────────────────
            // We push BOTH the canonical booking_form_submit event (so the
            // same GTM trigger that fires for the booking form also fires
            // here — keeps the conversion tag mapping simple) AND a
            // form-specific enquiry_form_submit event for segmentation.
            // fireLead also runs Enhanced Conversions hashing.
            try {
                await fireLead({
                    service: (templateParams.service as string) || 'enquiry',
                    email: templateParams.from_email as string,
                    phone: templateParams.phone as string,
                    page: window.location.pathname,
                });
            } catch (trackingErr) {
                console.warn('Enquiry tracking failed (non-blocking):', trackingErr);
            }

            if (typeof window !== 'undefined' && (window as any).dataLayer) {
                (window as any).dataLayer.push({
                    event: 'enquiry_form_submit',
                    form_type: 'contact_enquiry_form',
                    service_type: templateParams.service,
                    page_path: window.location.pathname,
                    page_url: window.location.href,
                });
            }

            toast({
                title: "Message Sent Successfully!",
                description: "Thank you for contacting Y3 Smiles Dental. We'll get back to you soon.",
            });

            // Reset form
            (e.target as HTMLFormElement).reset();
        } catch (error) {
            console.error('Enquiry form submission error:', error);
            toast({
                title: "Error Sending Message",
                description: "Please try again or call us directly at your convenience.",
                variant: "destructive",
            });
        } finally {
            onSubmitComplete?.();
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={className}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                        First Name
                    </label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                </div>
                <div className="space-y-2">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                        Last Name
                    </label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    />
                </div>
            </div>

            <div className="mt-4 space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                />
            </div>

            <div className="mt-4 space-y-2">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                </label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                />
            </div>

            <div className="mt-4 space-y-2">
                <label htmlFor="service" className="block text-sm font-medium text-gray-700">
                    Service Required
                </label>
                <select
                    id="service"
                    name="service"
                    required
                    defaultValue={defaultService || ""}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                >
                    <option value="">Select a service...</option>
                    <option value="Children's Dentistry (CDBS)">Children's Dentistry (CDBS)</option>
                    <option value="Comprehensive Examination & Dental Hygiene">Comprehensive Examination & Dental Hygiene</option>
                    <option value="Crown and Bridge">Crown and Bridge</option>
                    <option value="Dental Fillings">Dental Fillings</option>
                    <option value="Dental Implants">Dental Implants</option>
                    <option value="Dentures">Dentures</option>
                    <option value="Emergency Dentistry">Emergency Dentistry</option>
                    <option value="Fluoride Treatments">Fluoride Treatments</option>
                    <option value="Mouthguards">Mouthguards</option>
                    <option value="Periodontal Therapy">Periodontal Therapy</option>
                    <option value="Pits and Fissure Sealants">Pits and Fissure Sealants</option>
                    <option value="Root Canal Therapy">Root Canal Therapy</option>
                    <option value="TMD and Bruxism Treatment">TMD and Bruxism Treatment</option>
                    <option value="Tooth Extraction">Tooth Extraction</option>
                    <option value="Wisdom Teeth Removal">Wisdom Teeth Removal</option>
                    <option value="Teeth Whitening">Teeth Whitening</option>
                    <option value="Sedation Dentistry">Sedation Dentistry</option>
                    <option value="Other">Other</option>
                </select>
            </div>

            <div className="mt-4 space-y-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                </label>
                <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                ></textarea>
            </div>

            {/* Privacy Policy and Consent */}
            <div className="mt-4 space-y-3">
                <div className="flex items-start space-x-2">
                    <input
                        type="checkbox"
                        id="privacy-consent"
                        name="privacyConsent"
                        required
                        className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="privacy-consent" className="text-sm text-gray-700">
                        I agree to the{" "}
                        <Link to="/privacy-policy" className="text-primary hover:underline" target="_blank">
                            Privacy Policy
                        </Link>{" "}
                        and consent to the collection and use of my personal information for the purpose of responding to my enquiry.
                    </label>
                </div>

                <div className="flex items-start space-x-2">
                    <input
                        type="checkbox"
                        id="marketing-consent"
                        name="marketingConsent"
                        className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="marketing-consent" className="text-sm text-gray-700">
                        I would like to receive information about dental services, promotions, and health tips from Y3 Smiles Dental (optional).
                    </label>
                </div>
            </div>

            <div className="mt-6">
                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-4 bg-booking-gold text-white font-semibold rounded-lg hover:bg-[#A4846F] transition-gentle disabled:opacity-50"
                >
                    {submitting ? 'Sending...' : 'Send Enquiry'}
                </button>
            </div>
        </form>
    );
};

export default EmailJSForm;